import { baseApi } from "@/shared/api";
import type { SigningActionResponse } from "@/shared/types";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type {
  VehicleHandoverResponse,
  VehicleHandoverAct,
  VehicleHandoverListResponse,
  VehicleHandoverApiPayload,
} from "../types";

export const vehicleHandoverApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getVehicleHandover: builder.query<VehicleHandoverResponse, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-handovers/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "VehicleHandovers" as const, id }],
    }),

    getVehicleHandoverList: builder.query<VehicleHandoverAct[], void>({
      query: () => ({
        url: `/api/legal/vehicle-handovers/`,
        method: "GET",
      }),
      transformResponse: (response: VehicleHandoverListResponse): VehicleHandoverAct[] => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "VehicleHandovers" as const, id })),
              { type: "VehicleHandovers" as const, id: "LIST" },
            ]
          : [{ type: "VehicleHandovers" as const, id: "LIST" }],
    }),

    submitVehicleHandoverForSigning: builder.mutation<SigningActionResponse, { id: number; cms_signature: string; file_base64: string }>({
      query: ({ id, cms_signature, file_base64 }) => ({
        url: `/api/legal/vehicle-handovers/${id}/submit-for-signing/`,
        method: "POST",
        body: { cms_signature, file_base64 },
        credentials: "include" as const,
      }),
      invalidatesTags: () => [
        { type: "VehicleHandovers" as const, id: "LIST" },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          vehicleHandoverApi.util.updateQueryData("getVehicleHandover", id, (draft) => {
            draft.signing_status = 1;
          }),
        );
      },
    }),

    downloadVehicleHandoverPreview: builder.mutation<FileResponse, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-handovers/${id}/preview/`,
        method: "GET",
        responseHandler: fileResponseHandler,
      }),
    }),

    createVehicleHandover: builder.mutation<VehicleHandoverResponse, VehicleHandoverApiPayload>({
      query: (body) => ({
        url: "/api/legal/vehicle-handovers/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "VehicleHandovers" as const, id: "LIST" },
        { type: "VehicleRentals" as const, id: "LIST" },
      ],
    }),

    deleteVehicleHandover: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/legal/vehicle-handovers/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "VehicleHandovers" as const, id },
        { type: "VehicleHandovers" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetVehicleHandoverQuery,
  useGetVehicleHandoverListQuery,
  useSubmitVehicleHandoverForSigningMutation,
  useDownloadVehicleHandoverPreviewMutation,
  useCreateVehicleHandoverMutation,
  useDeleteVehicleHandoverMutation,
} = vehicleHandoverApi;

